import { AlertChunky } from '@eventbrite/eds-iconography';
import { dangerousGettext, gettext } from '@eventbrite/i18n';
import { Button, InlineAlert } from '@eventbrite/marmalade';
import React, { useEffect, useState } from 'react';
import { isSafari } from '../../utils/browser';
import styles from './StorageAccess.module.scss';

// This component will ask for user permission to access storage, such as cookies. This is needed when embedding the checkout widget on a third-party website.
// Refs: https://webkit.org/blog/11545/updates-to-the-storage-access-api/, https://developer.mozilla.org/en-US/docs/Web/API/Storage_Access_API
export interface StorageAccessProps {
    handleOnClose: () => void;
    isStorageAccessBannerEnabled: boolean;
}

const StorageAccess: React.FC<StorageAccessProps> = ({
    handleOnClose,
    isStorageAccessBannerEnabled,
}) => {
    const [hasStorageAccess, setHasStorageAccess] = useState<boolean>(true);
    const [popUpBlockedHref, setPopUpBlockedHref] = useState<string>('');

    useEffect(() => {
        document?.hasStorageAccess?.().then(setHasStorageAccess);
    }, []);

    if (hasStorageAccess) {
        return null;
    }

    const handleOnClick = () => {
        document
            .requestStorageAccess()
            // User granted permission, reload the page to access cookies
            .then(() => window.location.reload())
            // Many use cases here: user denied request, browser doesn't support API, used in an unsecured context, user didn't interact first-party on Safari, etc.
            .catch(() => {
                window.open(window.location.href, 'checkout');
                setPopUpBlockedHref(window.location.href);
            });
    };

    const popUpBlockedDescription = () => ({
        __html: dangerousGettext(
            "If you still can't continue, <a href='%(popUpBlockedHref)s' target=checkout>open a new tab</a>.",
            {
                popUpBlockedHref,
            },
        ).toString(),
    });

    return isSafari() && isStorageAccessBannerEnabled ? (
        <section
            role="dialog"
            className={styles.storageAccess}
            aria-label={gettext('Cookie access').toString()}
            aria-describedby="storage-access-description"
        >
            <InlineAlert
                description={
                    <>
                        <p
                            data-testid="storage-access-description"
                            id="storage-access-description"
                        >
                            {gettext(
                                "We're sorry, we can't display checkout because our cookies are blocked. To checkout, allow Eventbrite's cookies and reload the page to try again.",
                            )}
                        </p>
                        <p
                            data-testid="storage-access-blocked-popup-copy"
                            hidden={!popUpBlockedHref}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={popUpBlockedDescription()}
                        ></p>
                    </>
                }
                variant="danger"
                closeOnClick={handleOnClose}
                title={gettext('Allow cookies to complete your purchase')}
                icon={<AlertChunky />}
                cta={
                    <Button onClick={handleOnClick} variant="ghost">
                        {gettext('Allow cookies')}
                    </Button>
                }
            ></InlineAlert>
        </section>
    ) : null;
};

export default StorageAccess;
